import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type {
  CommandRequest,
  CommandResult,
  EditorSnapshot,
  LiveAction,
  SelectionMode,
  SelectionState,
} from "../types";
import { applyCommand, createInitialSnapshot } from "./commands";

interface EditorContextValue {
  snapshot: EditorSnapshot;
  getSnapshot: () => EditorSnapshot;
  execute: (request: CommandRequest, options?: { live?: boolean; skipHistory?: boolean }) => Promise<CommandResult>;
  setSelection: (selection: SelectionState) => void;
  setSelectionMode: (mode: SelectionMode) => void;
  setTool: (tool: EditorSnapshot["tools"]["tool"]) => void;
  setSpace: (space: EditorSnapshot["tools"]["space"]) => void;
  setDisplay: (display: EditorSnapshot["tools"]["display"]) => void;
  setQuadView: (value: boolean) => void;
  setPivotEditing: (value: boolean) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  liveAction?: LiveAction;
  updateLiveAction: (params: Record<string, number | boolean | string>) => Promise<CommandResult | null>;
  commitLiveAction: () => void;
  replaceSnapshot: (snapshot: EditorSnapshot, clearHistory?: boolean) => void;
  lastMessage: string;
}

const EditorContext = createContext<EditorContextValue | null>(null);

const actionTypeForCommand = (type: string): LiveAction["type"] | undefined => ({
  "mesh.extrude": "extrude",
  "mesh.bevel": "bevel",
  "mesh.insertEdgeLoop": "edgeLoop",
  "uv.projectBox": "boxUV",
  "uv.projectPlanar": "planarUV",
})[type] as LiveAction["type"] | undefined;

export function EditorProvider({ children }: { children: React.ReactNode }) {
  const [snapshot, setSnapshot] = useState<EditorSnapshot>(() => createInitialSnapshot());
  const snapshotRef = useRef(snapshot);
  const [past, setPast] = useState<EditorSnapshot[]>([]);
  const [future, setFuture] = useState<EditorSnapshot[]>([]);
  const [liveAction, setLiveAction] = useState<LiveAction>();
  const [lastMessage, setLastMessage] = useState("Ready — Maya controls active.");

  useEffect(() => { snapshotRef.current = snapshot; }, [snapshot]);

  const commitLiveAction = useCallback(() => setLiveAction(undefined), []);
  const getSnapshot = useCallback(() => snapshotRef.current, []);

  const execute = useCallback(async (
    request: CommandRequest,
    options: { live?: boolean; skipHistory?: boolean } = {},
  ): Promise<CommandResult> => {
    const base = snapshotRef.current;
    const applied = applyCommand(base, request);
    if (!applied.result.ok) {
      setLastMessage(applied.result.error?.message ?? "The command could not be completed.");
      return applied.result;
    }
    const isMutation = applied.snapshot.scene.revision !== base.scene.revision;
    if (isMutation && !options.skipHistory) {
      setPast((items) => [...items.slice(-99), structuredClone(base)]);
      setFuture([]);
    }
    snapshotRef.current = applied.snapshot;
    setSnapshot(applied.snapshot);
    const actionType = actionTypeForCommand(request.type);
    if (actionType && !options.live) {
      setLiveAction({
        id: request.id,
        type: actionType,
        label: applied.label ?? request.type,
        objectId: ((request.args as Record<string, unknown>).objectId as string | undefined) ?? base.scene.activeObjectId!,
        componentIds: ((request.args as Record<string, unknown>).componentIds as number[] | undefined) ?? base.selection.componentIds,
        params: structuredClone(request.args) as Record<string, unknown>,
        baseSnapshot: structuredClone(base),
      });
    } else if (isMutation && !options.live) {
      setLiveAction(undefined);
    }
    setLastMessage(applied.result.warnings[0] ?? `${applied.label ?? request.type} complete.`);
    return applied.result;
  }, []);

  const updateLiveAction = useCallback(async (params: Record<string, unknown>) => {
    if (!liveAction) return null;
    const combined = { ...liveAction.params, ...params, objectId: liveAction.objectId, componentIds: liveAction.componentIds };
    const commandType = {
      extrude: "mesh.extrude",
      bevel: "mesh.bevel",
      edgeLoop: "mesh.insertEdgeLoop",
      boxUV: "uv.projectBox",
      planarUV: "uv.projectPlanar",
    }[liveAction.type];
    const applied = applyCommand(liveAction.baseSnapshot, {
      id: liveAction.id,
      type: commandType,
      args: combined,
    });
    if (!applied.result.ok) {
      setLastMessage(applied.result.error?.message ?? "Could not update the current action.");
      return applied.result;
    }
    snapshotRef.current = applied.snapshot;
    setSnapshot(applied.snapshot);
    setLiveAction({ ...liveAction, params: combined });
    setLastMessage(applied.result.warnings[0] ?? `${liveAction.label} updated.`);
    return applied.result;
  }, [liveAction]);

  const setSelection = useCallback((selection: SelectionState) => {
    setLiveAction(undefined);
    const applied = applyCommand(snapshotRef.current, { id: crypto.randomUUID(), type: "selection.set", args: { selection } });
    snapshotRef.current = applied.snapshot;
    setSnapshot(applied.snapshot);
  }, []);
  const setSelectionMode = useCallback((mode: SelectionMode) => {
    setLiveAction(undefined);
    const current = snapshotRef.current;
    const selection = { ...current.selection, mode, componentIds: [], seedComponentId: undefined };
    const applied = applyCommand(current, { id: crypto.randomUUID(), type: "selection.set", args: { selection } });
    snapshotRef.current = applied.snapshot;
    setSnapshot(applied.snapshot);
  }, []);
  const updateTools = useCallback((patch: Partial<EditorSnapshot["tools"]>) => {
    const applied = applyCommand(snapshotRef.current, { id: crypto.randomUUID(), type: "tool.set", args: { tools: patch } });
    snapshotRef.current = applied.snapshot;
    setSnapshot(applied.snapshot);
  }, []);

  const undo = useCallback(() => {
    setPast((items) => {
      if (!items.length) return items;
      const previous = items[items.length - 1];
      setFuture((next) => [structuredClone(snapshotRef.current), ...next.slice(0, 99)]);
      snapshotRef.current = previous;
      setSnapshot(previous);
      setLiveAction(undefined);
      setLastMessage("Undo.");
      return items.slice(0, -1);
    });
  }, []);
  const redo = useCallback(() => {
    setFuture((items) => {
      if (!items.length) return items;
      const following = items[0];
      setPast((previous) => [...previous.slice(-99), structuredClone(snapshotRef.current)]);
      snapshotRef.current = following;
      setSnapshot(following);
      setLiveAction(undefined);
      setLastMessage("Redo.");
      return items.slice(1);
    });
  }, []);
  const replaceSnapshot = useCallback((next: EditorSnapshot, clearHistory = true) => {
    snapshotRef.current = next;
    setSnapshot(next);
    setLiveAction(undefined);
    if (clearHistory) { setPast([]); setFuture([]); }
    setLastMessage("Project loaded.");
  }, []);

  const value = useMemo<EditorContextValue>(() => ({
    snapshot,
    getSnapshot,
    execute,
    setSelection,
    setSelectionMode,
    setTool: (tool) => updateTools({ tool }),
    setSpace: (space) => updateTools({ space }),
    setDisplay: (display) => updateTools({ display }),
    setQuadView: (quadView) => updateTools({ quadView }),
    setPivotEditing: (pivotEditing) => updateTools({ pivotEditing }),
    undo,
    redo,
    canUndo: past.length > 0,
    canRedo: future.length > 0,
    liveAction,
    updateLiveAction,
    commitLiveAction,
    replaceSnapshot,
    lastMessage,
  }), [snapshot, getSnapshot, execute, setSelection, setSelectionMode, updateTools, undo, redo, past.length, future.length, liveAction, updateLiveAction, commitLiveAction, replaceSnapshot, lastMessage]);

  return <EditorContext.Provider value={value}>{children}</EditorContext.Provider>;
}

export function useEditor(): EditorContextValue {
  const value = useContext(EditorContext);
  if (!value) throw new Error("useEditor must be used inside EditorProvider.");
  return value;
}
